import React, { useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import KpiPickerModal from "./KpiPickerModal";

/**
 * KPI composer for the create/edit assessment page. Owns the picker
 * modal state and the top-level-vs-sub add flow; the parent page owns
 * the flat `kpis` array (rows without `parent_kpi_id` are top-level;
 * rows with it are children of that id). Depth is capped at 1: a row
 * with a parent cannot itself become a parent.
 */
const KpiComposer = ({ kpis, setKpis, kpiCatalog }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [subContext, setSubContext] = useState(null);

  const topLevel = useMemo(
    () => kpis.filter((k) => k.parent_kpi_id == null),
    [kpis]
  );
  const childrenByParentId = useMemo(() => {
    const map = new Map();
    for (const k of kpis) {
      if (k.parent_kpi_id == null) continue;
      const arr = map.get(k.parent_kpi_id) ?? [];
      arr.push(k);
      map.set(k.parent_kpi_id, arr);
    }
    return map;
  }, [kpis]);

  const toggleMandatory = (kpiId) => {
    setKpis((prev) =>
      prev.map((k) =>
        k.id === kpiId ? { ...k, is_mandatory: !k.is_mandatory } : k
      )
    );
  };

  const setWeightage = (kpiId, rawValue) => {
    const trimmed = String(rawValue ?? "").trim();
    setKpis((prev) =>
      prev.map((k) =>
        k.id === kpiId ? { ...k, weightage: trimmed === "" ? null : trimmed } : k
      )
    );
  };

  const removeKpi = (kpiId) => {
    setKpis((prev) => {
      const target = prev.find((k) => k.id === kpiId);
      if (!target) return prev;
      if (target.parent_kpi_id == null) {
        return prev.filter(
          (k) => k.id !== kpiId && k.parent_kpi_id !== kpiId
        );
      }
      return prev.filter((k) => k.id !== kpiId);
    });
  };

  const openTopLevelPicker = () => {
    setSubContext(null);
    setPickerOpen(true);
  };

  const openSubPicker = (parentRow) => {
    setSubContext(parentRow);
    setPickerOpen(true);
  };

  const closePicker = () => {
    setPickerOpen(false);
    setSubContext(null);
  };

  const applyTopLevelPick = (objs) => {
    setKpis((prev) => {
      const prevById = new Map(prev.map((k) => [k.id, k]));
      const objsIds = new Set(objs.map((o) => o.id));
      const kept = prev.filter((k) => objsIds.has(k.id));
      const additions = objs
        .filter((o) => !prevById.has(o.id))
        .map((o) => ({
          id: o.id,
          title: o.title || kpiCatalog[o.id] || "",
          is_mandatory: false,
          weightage: null,
          default_weightage: o.default_weightage ?? null,
          parent_kpi_id: null,
        }));
      const combined = [...additions, ...kept];
      const combinedIds = new Set(combined.map((k) => k.id));
      return combined.filter(
        (k) => k.parent_kpi_id == null || combinedIds.has(k.parent_kpi_id)
      );
    });
  };

  const applySubPick = (objs, parent) => {
    setKpis((prev) => {
      const existingIds = new Set(prev.map((k) => k.id));
      const currentChildrenIds = new Set(
        prev
          .filter((k) => k.parent_kpi_id === parent.id)
          .map((k) => k.id)
      );
      const additions = [];
      for (const o of objs) {
        if (o.id === parent.id) continue;
        if (currentChildrenIds.has(o.id)) continue;
        if (existingIds.has(o.id)) continue;
        additions.push({
          id: o.id,
          title: o.title || kpiCatalog[o.id] || "",
          is_mandatory: false,
          weightage: null,
          default_weightage: o.default_weightage ?? null,
          parent_kpi_id: parent.id,
        });
      }
      return [...prev, ...additions];
    });
  };

  const onPickerConfirm = (objs) => {
    if (subContext) applySubPick(objs, subContext);
    else applyTopLevelPick(objs);
    closePicker();
  };

  const pickerSelectedIds = subContext
    ? (childrenByParentId.get(subContext.id) || []).map((k) => k.id)
    : kpis.map((k) => k.id);

  const renderRow = (k, idx, isChild) => (
    <tr key={`${isChild ? "c" : "p"}-${k.id}`}>
      <td>{isChild ? "" : idx + 1}</td>
      <td style={{ paddingLeft: isChild ? 32 : undefined }}>
        {isChild ? "↳ " : ""}
        {k.title || "(untitled)"}
      </td>
      <td>
        <div className="form-check m-0">
          <input
            type="checkbox"
            className="form-check-input"
            id={`mandatory-${k.id}`}
            checked={!!k.is_mandatory}
            onChange={() => toggleMandatory(k.id)}
          />
          <label className="form-check-label" htmlFor={`mandatory-${k.id}`}>
            Mandatory
          </label>
        </div>
      </td>
      <td>
        <input
          type="number"
          min="0"
          step="0.01"
          className="form-control form-control-sm"
          value={k.weightage ?? ""}
          onChange={(e) => setWeightage(k.id, e.target.value)}
          placeholder={
            k.default_weightage != null ? String(k.default_weightage) : "—"
          }
          title={
            k.default_weightage != null
              ? `Default: ${k.default_weightage}. Blank = use default.`
              : "No default. Blank = no weightage."
          }
          style={{ width: 90, fontSize: 12 }}
        />
      </td>
      <td className="text-center">
        {!isChild && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary me-2"
            style={{ fontSize: 11, padding: "1px 8px" }}
            onClick={() => openSubPicker(k)}
            title="Add sub-KPI under this KPI"
          >
            + Sub
          </button>
        )}
        <span
          style={{ cursor: "pointer", color: "red" }}
          onClick={() => removeKpi(k.id)}
          title="Remove"
        >
          <i className="fas fa-trash"></i>
        </span>
      </td>
    </tr>
  );

  return (
    <>
      <div className="mb-3">
        <label className="form-label fw-semibold text-dark">
          <i className="fas fa-tasks text-primary me-2"></i> KPIs
        </label>

        {topLevel.length === 0 ? (
          <div className="mb-2">
            <small className="text-muted">No KPIs selected yet.</small>
          </div>
        ) : (
          <div className="table_f border-top mt-2 mb-2 auto_scroll_by_design">
            <Table striped bordered className="mt-2 mb-0">
              <thead>
                <tr className="heading_color sticky_header">
                  <th style={{ width: 50 }}>Sr.</th>
                  <th>KPI</th>
                  <th style={{ width: 140 }}>Mandatory</th>
                  <th style={{ width: 110 }}>Weightage</th>
                  <th style={{ width: 130 }}></th>
                </tr>
              </thead>
              <tbody>
                {topLevel.flatMap((parent, idx) => [
                  renderRow(parent, idx, false),
                  ...(childrenByParentId.get(parent.id) || []).map((c) =>
                    renderRow(c, idx, true)
                  ),
                ])}
              </tbody>
            </Table>
          </div>
        )}

        <button
          type="button"
          className="link_bal_next_cancel"
          onClick={openTopLevelPicker}
        >
          <i className="fas fa-plus-square me-2"></i>{" "}
          {topLevel.length ? "Edit KPI selection" : "Pick KPIs"}
        </button>
      </div>

      <KpiPickerModal
        show={pickerOpen}
        onClose={closePicker}
        selectedIds={pickerSelectedIds}
        onConfirm={onPickerConfirm}
        title={subContext ? `Add sub-KPIs to "${subContext.title}"` : undefined}
      />
    </>
  );
};

export default KpiComposer;
